/* eslint-disable react/prop-types */
/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/ban-ts-comment */

import React, { ReactElement, useEffect } from "react";

import { ErrorMessage } from "@hookform/error-message";
import clsx from "clsx";
import get from "lodash/get";
import styled, { css } from "styled-components";

import { Text, TextProps, Subtitle as BaseSubtitle, TitleProps } from "~/components/Text";
import * as Bulma from "~/types/bulma";

// eslint-disable-next-line no-shadow
export type InputEvent = React.ChangeEvent<HTMLInputElement>;
export type FormEvent = React.ChangeEvent<HTMLFormElement>;

export function usePreventScrollInputNumber() {
  useEffect(() => {
    function handler(event: WheelEvent) {
      if (event.target && (event.target as any).type === "number") {
        (event.target as any).blur();
      }
    }
    document.addEventListener("wheel", handler);
    return function cleanup() {
      document.removeEventListener("wheel", handler);
    };
  });
}

export type InputFieldType =
  | "button"
  | "checkbox"
  | "color"
  | "date"
  | "datetime-local"
  | "email"
  | "file"
  | "hidden"
  | "image"
  | "month"
  | "number"
  | "password"
  | "radio"
  | "range"
  | "reset"
  | "search"
  | "submit"
  | "tel"
  | "text"
  | "time"
  | "url"
  | "week";

type Autocomplete =
  | "off"
  | "on"
  | "name"
  | "honorific-prefix"
  | "given-name"
  | "additional-name"
  | "family-name"
  | "honorific-suffix"
  | "nickname"
  | "email"
  | "username"
  | "new-password"
  | "current-password"
  | "one-time-code"
  | "organization-title"
  | "organization"
  | "street-address"
  | "address-line1"
  | "address-line2"
  | "address-line3"
  | "address-level1"
  | "address-level2"
  | "address-level3"
  | "address-level4"
  | "country"
  | "country-name"
  | "postal-code"
  | "transaction-currency"
  | "transaction-amount"
  | "language"
  | "bday"
  | "sex"
  | "tel"
  | "tel-extension"
  | "impp"
  | "url"
  | "photo"
  | "hq-street-address"
  | "hq-building-number"
  | "hq-city"
  | "hq-country"
  | "hq-postal-code"
  | "hq-region";

export type TypedDefaultInput = { type?: InputFieldType; autoComplete?: Autocomplete };

// export type LabelType<O extends object = {}> = StyledComponent<"label", DefaultTheme, O>;
// export type InputType<O extends object = {}> = StyledComponent<"input", DefaultTheme, O>;
// export type LegendType<O extends object = {}> = StyledComponent<"legend", DefaultTheme, O>;
// export type TextareaType<O extends object = {}> = StyledComponent<"textarea", DefaultTheme, O>;

export const Asterisk = styled.span.attrs({ children: "*" })``;

export const Fieldset = styled.fieldset`
  display: contents;
`;

export type FieldProps = Bulma.Gaping & Bulma.Addons & { grouped?: boolean | null };
export const Field = styled.div.attrs<FieldProps>((props) => ({
  className: clsx({
    field: true,
    [`has-addons`]: props.addons,
    [`is-grouped`]: props.grouped,
    [`is-marginless`]: props.marginless,
    [`is-paddingless`]: props.paddingless,
  }),
}))<FieldProps>`
  ${(props) =>
    !props.addons &&
    css`
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      align-items: stretch;

      /* Force it */
      margin-bottom: 0.75rem;
      /* Move bottom since Help component uses space */
      padding-bottom: 0;
    `}
`;

export type LabelProps = TextProps;
export const Label = styled(Text as "label").attrs<LabelProps>({
  as: "label",
  className: clsx("label"),
})<LabelProps>`
  display: flex;
`;
Label.defaultProps = {
  weight: "bolder",
  transform: "uppercase",
  size: "7",
};

export const LabelSub = styled(Text as "p")<TextProps>`
  margin: 0.75em 0;
`;
LabelSub.defaultProps = {
  // @ts-ignore
  as: "p",
};

export type LabelRadioProps = LabelProps;
export const LabelRadio = styled(Label as "legend").attrs<LabelRadioProps>({ as: "legend" })<LabelRadioProps>``;

export type ControlProps = Bulma.Loadable & { expanded?: boolean | null; $loading: boolean };
export const Control: any = styled.div.attrs<ControlProps>((props) => ({
  className: clsx({
    [`control`]: true,
    [`is-loading`]: props.loading,
    [`is-expanded`]: props.expanded,
  }),
}))<ControlProps>``;

export type ControlInputProps = TextProps & Bulma.Common & Bulma.Roundable & Bulma.Static & TypedDefaultInput;
export const ControlInput = styled(Text as "input").attrs<ControlInputProps>((props) => ({
  as: "input",
  className: clsx({
    [`input`]: true,
    [`is-static`]: Boolean(props.static),
    [`is-rounded`]: Boolean(props.rounded),
    [`is-${props.kind}`]: Boolean(props.kind),
    [`is-${props.sized}`]: Boolean(props.sized),
  }),
}))<ControlInputProps>``;

export type ControlInputTextareaProps = TextProps & Bulma.Common;
export const ControlInputTextarea = styled(Text as "textarea").attrs<ControlInputTextareaProps>((props) => ({
  as: "textarea",
  className: clsx({
    [`textarea`]: true,
    [`is-${props.kind}`]: Boolean(props.kind),
    [`is-${props.sized}`]: Boolean(props.sized),
  }),
}))<ControlInputTextareaProps>``; //  as TextareaType<ControlInputTextareaProps> & TextType;

export const ControlRadio = styled(Control)<{ orientation?: "horizontal" | "vertical" }>`
  display: flex;
  ${(props) =>
    props.orientation === "horizontal" &&
    css`
      flex-direction: row;
      justify-content: flex-start;
      align-items: center;
    `}
  ${(props) =>
    props.orientation === "vertical" &&
    css`
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    `}
`;
ControlRadio.defaultProps = {
  orientation: "vertical",
};
export const ControlRadioLabel = styled.label.attrs({ className: clsx("radio") })`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: baseline;

  &&& {
    margin-right: calc(0.75rem * 2);
  }
`;
export const ControlRadioInput = styled.input.attrs({ type: "radio" })``;
export const ControlRadioInputText = styled(Text)`
  margin-left: 0.75rem;
`;

export const ControlSelect = styled.div.attrs<Bulma.Common>((props) => ({
  className: clsx({
    [`select`]: true,
    [`is-fullwidth`]: true,
    [`is-${props.kind}`]: Boolean(props.kind),
    [`is-${props.sized}`]: Boolean(props.sized),
  }),
}))<Bulma.Common>``;
export const ControlSelectInput = styled.select``;

export const Help = styled.p.attrs<Bulma.Kinded>((props) => ({
  className: clsx({
    ["help"]: true,
    [`is-${props.kind}`]: Boolean(props.kind),
  }),
}))<Bulma.Kinded>``;

export const CheckboxLabel = styled.label.attrs({ className: clsx("checkbox") })`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: baseline;

  span {
    margin-left: 0.75rem;
  }
`;

export const CheckboxInput = styled.input.attrs({ type: "checkbox" })``;
export const CheckboxText = styled(Text)``;

type FormTitle = Omit<TitleProps, "as">;
export const Title = styled(BaseSubtitle as "h2")<FormTitle>``;
Title.defaultProps = {
  // @ts-ignore
  as: "h2",
  weight: "bolder",
};
export const Subtitle = styled(BaseSubtitle as "h3")<FormTitle>``;
Subtitle.defaultProps = {
  // @ts-ignore
  as: "h3",
  size: "6",
  weight: "bold",
};

export const required = {
  value: true,
  message: "This field is required",
};

export type EasyFieldProps = ControlInputProps & {
  name: string;
  label?: string;
  labelSub?: string;
  afterLabelElement?: JSX.Element;
  help?: string;
  placeholder?: string;
  validation?: {
    name: string;
    errors: string[];
    warnings: string[];
    optionals: string[];
    ValidationMessages: any;
  };
  TooltipComponent?: ({ id }: { id: string }) => ReactElement;
  errors?: any;
  required?: boolean;
  static?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  value?: any;
  disabled?: boolean;
  className?: any;
  style?: any;
  inputStyle?: any;
  children?: any; // for select
  min?: number;
  max?: number;
  step?: number;
  list?: string;
  initialValue?: any;
  defaultValue?: any;
};

export const EasyField = React.forwardRef<any, EasyFieldProps>(
  (
    {
      name,
      label = name,
      afterLabelElement,
      labelSub,
      help,
      validation,
      TooltipComponent,
      errors,
      loading,
      required: isRequired,
      className,
      style,
      inputStyle,
      ...props
    },
    ref: any,
  ) => {
    const id = `easy-${name}`;

    let errorMessage, warningMessage, optionalMessage;
    if (validation) {
      errorMessage = validation.errors.includes(validation.name + "Missing")
        ? validation.ValidationMessages.required
        : undefined;
      optionalMessage = validation.optionals.includes(validation.name)
        ? validation.ValidationMessages.optional
        : undefined;
      warningMessage = validation.warnings.includes(validation.name + "Missing")
        ? validation.ValidationMessages.requiredInCounterparty
        : undefined;
    }

    const error = get(errors, name);
    const kind = error || errorMessage ? "danger" : warningMessage || optionalMessage ? "warning" : undefined;

    return (
      <Field {...{ className, style }}>
        <Label htmlFor={name}>
          {label} {isRequired && <Asterisk />} {afterLabelElement}
          {labelSub && (
            <>
              <LabelSub transform={null} weight="normal">
                {labelSub}
              </LabelSub>
            </>
          )}
        </Label>

        <Control $loading={Boolean(loading)} data-tip={!!TooltipComponent} data-for={TooltipComponent ? id : undefined}>
          <ControlInput
            id={id}
            kind={kind}
            name={name}
            style={inputStyle}
            aria-invalid={error || errorMessage ? "true" : "false"}
            aria-required={isRequired ? "true" : "false"}
            aria-describedby={
              errors || errorMessage || warningMessage || optionalMessage ? `control-help-error-${id}` : undefined
            }
            {...(props as any)}
            ref={ref}
          />
        </Control>
        {!!TooltipComponent && <TooltipComponent id={id} />}
        {help && <Help>{help}</Help>}
        {kind && (
          <Help kind={kind} id={`control-help-error-${id}`}>
            {error ? (
              <ErrorMessage name={name} errors={errors} />
            ) : (
              [errorMessage, warningMessage, optionalMessage].filter((x) => !!x).join("<br />")
            )}
          </Help>
        )}
      </Field>
    );
  },
);
EasyField.displayName = "EasyField";

export const EasyFieldSelect = React.forwardRef<any, EasyFieldProps>(
  (
    {
      name,
      label = name,
      afterLabelElement,
      labelSub,
      help,
      validation,
      TooltipComponent,
      errors,
      loading,
      required: isRequired,
      className,
      style,
      ...props
    },
    ref: any,
  ) => {
    const id = `easy-${name}`;

    let errorMessage, warningMessage, optionalMessage;
    if (validation) {
      errorMessage = validation.errors.includes(validation.name + "Missing")
        ? validation.ValidationMessages.required
        : undefined;
      optionalMessage = validation.optionals.includes(validation.name)
        ? validation.ValidationMessages.optional
        : undefined;
      warningMessage = validation.warnings.includes(validation.name)
        ? validation.ValidationMessages.requiredInCounterparty
        : undefined;
    }

    const error = get(errors, name);
    const kind = error || errorMessage ? "danger" : warningMessage || optionalMessage ? "warning" : undefined;

    return (
      <Field {...{ className, style }}>
        <Label htmlFor={name}>
          {label} {isRequired && <Asterisk />} {afterLabelElement}
          {labelSub && (
            <>
              <LabelSub transform={null} weight="normal">
                {labelSub}
              </LabelSub>
            </>
          )}
        </Label>

        <Control $loading={Boolean(loading)} data-tip={!!TooltipComponent} data-for={TooltipComponent ? id : undefined}>
          <ControlSelect kind={kind}>
            <ControlSelectInput
              id={id}
              name={name}
              aria-invalid={error || errorMessage ? "true" : "false"}
              aria-required={isRequired ? "true" : "false"}
              aria-describedby={
                errors || errorMessage || warningMessage || optionalMessage ? `control-help-error-${id}` : undefined
              }
              {...(props as any)}
              ref={ref}
            />
          </ControlSelect>
        </Control>
        {!!TooltipComponent && <TooltipComponent id={id} />}
        {help && <Help>{help}</Help>}
        {kind && (
          <Help kind={kind} id={`control-help-error-${id}`}>
            {error ? (
              <ErrorMessage name={name} errors={errors} />
            ) : (
              [errorMessage, warningMessage, optionalMessage].filter((x) => !!x).join("<br />")
            )}
          </Help>
        )}
      </Field>
    );
  },
);
EasyFieldSelect.displayName = "EasyFieldSelect";
